import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();
  
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 sm:pt-20">
      {/* Desktop Background */}
      <div 
        className="hidden sm:block absolute inset-0 z-0"
        style={{
          backgroundImage: `linear-gradient(rgba(62, 39, 35, 0.6), rgba(62, 39, 35, 0.6)), url('https://static.readdy.ai/image/b9dffa7b6b8fd92109fabc077dd82307/3f21147bbee8ca5108fbf2b2f0420820.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Mobile Background */}
      <div 
        className="block sm:hidden absolute inset-0 z-0"
        style={{
          backgroundImage: `linear-gradient(rgba(62, 39, 35, 0.6), rgba(62, 39, 35, 0.6)), url('https://static.readdy.ai/image/b9dffa7b6b8fd92109fabc077dd82307/f2c7cfc389acec463c9a4f27d7f9afb4.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full py-6 sm:py-0 flex flex-col justify-between min-h-[calc(100vh-4rem)] sm:min-h-0">
        <div className="flex-1 flex flex-col justify-start pt-4 sm:justify-center sm:pt-0 sm:flex-none sm:block">
          <div className="inline-block mb-2 sm:mb-6 px-3 sm:px-6 py-1 sm:py-2 bg-amber-600/90 backdrop-blur-sm rounded-full">
            <p className="text-white font-semibold text-[10px] sm:text-sm tracking-wide">{t('hero.badge')}</p>
          </div>
          
          <h2 className="text-2xl sm:text-5xl md:text-7xl font-bold text-white mb-1.5 sm:mb-6 px-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('hero.title')}
          </h2>
          
          <p className="text-sm sm:text-xl md:text-2xl text-amber-100 mb-1.5 sm:mb-4 max-w-3xl mx-auto leading-relaxed px-4">
            {t('hero.subtitle')}
          </p>
          
          <p className="text-xs sm:text-lg md:text-xl text-white/90 mb-3 sm:mb-10 max-w-2xl mx-auto px-4">
            {t('hero.description')}
          </p>
        </div>

        <div className="pb-2 sm:pb-0">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center items-center px-4 mb-3 sm:mb-10">
            <button 
              onClick={() => scrollToSection('menu')}
              className="w-full sm:w-auto px-5 sm:px-8 py-2.5 sm:py-4 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all duration-300 font-semibold text-sm sm:text-lg shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              {t('hero.exploreMenu')}
            </button>
            <button 
              onClick={() => scrollToSection('location')}
              className="w-full sm:w-auto px-5 sm:px-8 py-2.5 sm:py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-full hover:bg-white hover:text-amber-900 transition-all duration-300 font-semibold text-sm sm:text-lg whitespace-nowrap cursor-pointer"
            >
              {t('hero.visitUs')}
            </button>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-8 max-w-4xl mx-auto px-4 mb-2 sm:mb-0">
            <div className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-white/20">
              <div className="w-8 h-8 sm:w-14 sm:h-14 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-1.5 sm:mb-4">
                <i className="ri-fire-fill text-lg sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-white font-bold text-xs sm:text-xl mb-1 sm:mb-2">{t('hero.feature1Title')}</h3>
              <p className="text-amber-100 text-[9px] sm:text-sm hidden sm:block">{t('hero.feature1Desc')}</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-white/20">
              <div className="w-8 h-8 sm:w-14 sm:h-14 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-1.5 sm:mb-4">
                <i className="ri-award-fill text-lg sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-white font-bold text-xs sm:text-xl mb-1 sm:mb-2">{t('hero.feature2Title')}</h3>
              <p className="text-amber-100 text-[9px] sm:text-sm hidden sm:block">{t('hero.feature2Desc')}</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-white/20">
              <div className="w-8 h-8 sm:w-14 sm:h-14 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-1.5 sm:mb-4">
                <i className="ri-map-pin-fill text-lg sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-white font-bold text-xs sm:text-xl mb-1 sm:mb-2">{t('hero.feature3Title')}</h3>
              <p className="text-amber-100 text-[9px] sm:text-sm hidden sm:block">{t('hero.feature3Desc')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <button 
          onClick={() => scrollToSection('about')}
          className="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center animate-bounce cursor-pointer"
        >
          <i className="ri-arrow-down-line text-xl sm:text-3xl text-white"></i>
        </button>
      </div>
    </section>
  );
}
